import { MedplumClient } from "@medplum/core";
import { MedplumProvider, useMedplum } from "@medplum/react-hooks";
import { initTRPCClient } from "@repo/api";
import { type Permissions, PermissionsProvider, parseAccessPolicy, usePermissionsState } from "@repo/auth";
import { configureCallDiagnostics } from "@repo/conferencing/components";
import { FhirDataStoreProvider, onWriteError } from "@repo/data-store";
import { ThemeProvider } from "@repo/ui/components/theme-provider";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { toast } from "sonner";
import { panelStore } from "@/app/providers/panel-store";
import { showTRPCError } from "@/lib/trpc-error-toast";
import type { FetchLike } from "@/shared/lib/fetch-like";
import { routeTree } from "./routeTree.gen";
import "./styles.css";

// Hard fuse on auth failure. Medplum's `onUnauthenticated` only fires after
// its internal refresh-and-retry dance, and not on every 401 path — leaving
// room for callers (hooks, route guards, cached profile reads) to keep
// hammering `/auth/me` while the storm spirals. We wrap the global fetch
// instead: the first 401 to an authed endpoint clears Medplum state and
// bounces to `/login`. Guarded so we fire the side effect exactly once.
let authFailureHandled = false;
function isAuthedEndpoint(url: string): boolean {
  if (url.includes("/oauth2/token")) return false;
  if (url.includes("/auth/login")) return false;
  if (url.includes("/auth/register")) return false;
  return url.includes("/auth/me") || url.includes("/fhir/");
}
const authAwareFetch: FetchLike = async (input, init) => {
  const response = await fetch(input, init);
  if (response.status === 401 && !authFailureHandled) {
    const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
    if (isAuthedEndpoint(url) && window.location.pathname !== "/login") {
      authFailureHandled = true;
      medplum.clear();
      window.location.href = `/login?redirect=${encodeURIComponent(window.location.pathname)}`;
    }
  }
  return response;
};

// Initialize Medplum
const medplum = new MedplumClient({
  baseUrl: import.meta.env.VITE_MEDPLUM_BASE_URL || "http://localhost:8103",
  clientId: import.meta.env.VITE_MEDPLUM_CLIENT_ID,
  storagePrefix: "@medplum:provider:",
  fetch: authAwareFetch,
  onUnauthenticated: () => {
    // Backstop for paths that the fetch wrapper missed (e.g. Medplum-internal
    // calls that don't go through the global fetch). Same behavior.
    if (authFailureHandled) return;
    authFailureHandled = true;
    medplum.clear();
    if (window.location.pathname !== "/login") {
      window.location.href = `/login?redirect=${encodeURIComponent(window.location.pathname)}`;
    }
  },
});

// TanStack Query client — used for orchestrated tRPC reads (find availability, etc).
// Reactive collection reads still go through TanStack DB via @repo/data-store.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
  },
});

// Create router
const router = createRouter({
  routeTree,
  context: {
    // biome-ignore lint/style/noNonNullAssertion: context init placeholder, overridden at render
    medplum: undefined!,
    // biome-ignore lint/style/noNonNullAssertion: context init placeholder, overridden at render
    permissions: undefined!,
    profile: undefined,
  },
  defaultPreload: "intent",
});

// Close any open panel on navigation
router.subscribe("onResolved", (evt) => {
  if (evt.pathChanged) {
    panelStore.send({ type: "panel.close" });
  }
});

// Type registration
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const stopWriteErrors = onWriteError((message) => toast.error(message));
if (import.meta.hot) import.meta.hot.dispose(stopWriteErrors);

// Render after Medplum restores session
async function initApp() {
  await medplum.getInitPromise();
  configureCallDiagnostics(import.meta.env.VITE_API_URL || "http://localhost:8104/trpc", () =>
    medplum.getAccessToken(),
  );

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      medplum.refreshIfExpired().catch(() => {});
    }
  });

  // Initialize tRPC client. Domain-rule violations (PRECONDITION_FAILED, CONFLICT,
  // BAD_REQUEST) surface as a toast globally — callers don't wire their own.
  initTRPCClient({
    url: import.meta.env.VITE_API_URL || "http://localhost:8104/trpc",
    getAccessToken: async () => {
      // Refresh first — `getAccessToken` won't, so an idle-expired token would 500.
      await medplum.refreshIfExpired().catch(() => {});
      return medplum.getAccessToken() ?? null;
    },
    onError: showTRPCError,
  });

  // Default permissions (no access)
  let permissions = parseAccessPolicy(null, false);

  // Wait for session restoration
  const activeLogin = medplum.getActiveLogin();
  if (activeLogin) {
    medplum.setAccessToken(activeLogin.accessToken, activeLogin.refreshToken);
    try {
      await medplum.getProfileAsync();

      // Fetch /auth/me to get the computed AccessPolicy
      // This endpoint returns the effective policy with %profile substituted
      const membership = medplum.getProjectMembership();
      const isProjectAdmin = membership?.admin ?? false;

      try {
        const authMe = await medplum.get("auth/me");
        if (authMe.accessPolicy) {
          // Use the computed accessPolicy directly from /auth/me
          permissions = parseAccessPolicy(authMe.accessPolicy, isProjectAdmin);
        }
      } catch {
        // /auth/me failed despite a successful profile fetch — fall back to
        // default permissions but keep rendering. Token is still valid.
      }
    } catch {
      // Profile fetch failed (stale token, deleted project, etc). Without an
      // explicit redirect, the app would render in a broken state and other
      // hooks would re-hit /auth/me in a loop. Clear tokens and bounce to
      // /login deterministically — do NOT rely on Medplum's onUnauthenticated
      // callback firing here, since it's not guaranteed for every 401 path.
      medplum.clear();
      if (window.location.pathname !== "/login") {
        window.location.href = `/login?redirect=${encodeURIComponent(window.location.pathname)}`;
        return;
      }
    }
  }

  const rootElement = document.getElementById("root");
  if (rootElement) {
    const root = createRoot(rootElement);
    root.render(
      <StrictMode>
        <ThemeProvider defaultTheme="light" storageKey="provider-theme">
          <QueryClientProvider client={queryClient}>
            <MedplumProvider medplum={medplum}>
              <App initialPermissions={permissions} />
            </MedplumProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </StrictMode>,
    );
  }
}

function App({ initialPermissions }: { initialPermissions: Permissions }) {
  const medplum = useMedplum();
  const profile = medplum.getProfile();
  const { permissions, refetch, clear } = usePermissionsState(medplum, initialPermissions);

  return (
    <PermissionsProvider permissions={permissions} refetch={refetch} clear={clear}>
      {profile ? (
        <FhirDataStoreProvider canRead={permissions.canRead} enableSubscriptions={permissions.canWrite("Subscription")}>
          <RouterProvider router={router} context={{ medplum, permissions, profile }} />
        </FhirDataStoreProvider>
      ) : (
        <RouterProvider router={router} context={{ medplum, permissions, profile }} />
      )}
    </PermissionsProvider>
  );
}

initApp().catch(console.error);
